import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs/';

import { DynamicGridService } from './dynamic-grid.service';
import { SessionService } from '../../../core/services/session.service';
import { NotificationService } from '../../../core/services/notification.service';

declare var jQuery: any;

export interface DynamicGridFieldConfig<T = any> {
    type?: string;
    dictionaryUrl?: string;
    icon?: string | { [key: string]: string };
    route?: string;
    key?: string;
    cutOffWidth?: number;
    cutOffText?: boolean;
    defaultOption?: string;
    condition?: boolean;
    ratingMax?: number;
    template?: string;
    map?: { [key: string]: string };
    queryParams?: { [key: string]: string };
    tooltipKeyMap?: { [key: string]: string | ((item: T) => string) };
    actions?: {
        class?: string;
        icon?: string;
        translationKey?: string;
        label?: string;
        name?: string;
        condition?: (item: T) => boolean;
        tooltip?: (item: T) => string;
    }[];
    tooltipLink?: (item: T) => string;
    disableIf?: (item: T) => boolean;
    url?: (item: T) => string;
    disabled?: (item: T) => boolean;
    format?: (item: T) => string;
}

export interface DynamicGridField<T = any> {
    title?: string;
    name?: ((item: T) => string) | string;
    weight?: number;
    sortable?: boolean;
    hidden?: boolean;
    width?: number;
    checkboxTitle?: boolean;
    tooltip?: {
        key?: string;
    };
    config?: DynamicGridFieldConfig<T>;
    tooltipConfig?: {
        icon?: string;
        tooltipKey?: (item: T) => string;
        condition?: (item: T) => boolean;
    };
    currency?: (item: T) => string;
    function?: (item: T) => string;
    condition?: (item: T) => boolean;
    formatter?: (value: any, item: T) => string;
}

export interface BulkActionConfig<T = any> {
    key?: string;
    clearSelection?: Observable<void>;
    doNotShowActionLabel?: boolean;
    actions?: {
        enabled?: boolean;
        icon?: string;
        label?: string;
        show?: (items: T[]) => boolean;
    }[];
    getIds?: (items: T[]) => string[];
    onItemSelectionChange?: (item: T, boolean) => void;
    showCheckbox?: (item: T) => boolean;
    disableCheckbox?: (item: T) => boolean;
    disableIf?: (item: T) => boolean;
}

export interface DynamicGridComponentConfig {
    defaultSorted?: string[];
    allowMultiSort?: boolean;
    clearTitle?: string;
    allowClearSorting?: boolean;
    emptyMessage?: string;
    wrapText?: boolean;
    emptyMessageKey?: string;
    useDollarPrefixConvention?: boolean;
    allowEmptySelection?: boolean;
    listDelegate?: (params) => Observable<any[]>;
}

export class ActionClickEvent<T = any> {
    constructor(public action: string, public item: T) { }
}

export class ActionLinkEvent<T = any> {
    constructor(public item: T) { }
}

export class BulkActionClickEvent<T = any> {
    constructor(public action: string, public items: T[]) { }
}

@Component({
    selector: 'itpx-dynamic-grid',
    templateUrl: './dynamic-grid.component.html',
    styleUrls: ['./dynamic-grid.component.css'],
    animations: [
        trigger('slideInOut', [
            state('in', style({})),
            transition('void => *', [
                animate(100, keyframes([
                    style({ opacity: 0, offset: 0 }),
                    style({ opacity: 1, offset: 1 })
                ]))
            ])
        ])
    ]
})
export class DynamicGridComponent<T = any> implements OnInit, OnDestroy {

    @Input() url: string;
    @Input() message: string;
    @Input() fields: DynamicGridField<T>[];
    @Input() refresh: Observable<never>;
    @Input() filterRefresh: Observable<any>;
    @Input() bulkActionsConfig: BulkActionConfig<T>;
    @Input() config: DynamicGridComponentConfig;
    @Input() allSelected: Observable<any>;
    @Input() plurals: Array<string> = ['item', 'items'];
    @Input() templates: { [key: string]: TemplateRef<ElementRef> } = {};
    // @Input() selectedProductsCount: number = 0;
    @Input() preSelectedItems: any[];
    @Input() hideRefresh: boolean = false;
    @Input() showInlineList: boolean = true;
    @Input() infoMessageHTML: TemplateRef<ElementRef>;
    @Input() tableStyles: string = 'table table-striped table-bordered table-hover';
    @Input() searchDebounceTime: number = 400;

    @Output() actionClicked = new EventEmitter<ActionClickEvent<T>>();
    @Output() linkClicked = new EventEmitter<ActionLinkEvent<T>>();
    @Output() bulkActionClicked = new EventEmitter<BulkActionClickEvent<T>>();
    @Output() refreshClicked = new EventEmitter<any>();
    @Output() listSize = new EventEmitter<any>();
    @Output() clearSortingClicked = new EventEmitter<any>();
    @Output() noRecordSelected = new EventEmitter<boolean>();
    @Output() onPageChangedEvent = new EventEmitter<boolean>();
    @ViewChild('selectedAllCheckbox') selectedAllCheckbox: any;
    @ViewChild('htmlSpan') htmlSpan: ElementRef;
    @ViewChild('htmlMapWrapper') htmlMapWrapper: ElementRef;

    private rating = 4;

    private page: number = 1;
    private limit: number = 10;
    private filter: string = '';

    private items: Array<T> = [];
    private itemsCount: number;

    private currentSort: Map<string, number> = new Map();
    private timeout: any;

    private currentRoute: string;

    private isLoading: boolean = false;

    private columns: any = {};
    private selectedItems: any[] = [];
    private selectedIndices: boolean[];
    private selectedCount: number = 0;

    private sub: Subscription;
    private subSelected: Subscription;

    private amberDictionaries = {};
    private showActionSpan: boolean = false;
    private showEmptyMessage = false;

    // private selectedAllCheckbox: boolean = false;

    constructor(
        private service: DynamicGridService,
        private sessionService: SessionService,
        private notificationService: NotificationService,
        private router: Router,
        private route: ActivatedRoute,
        private el: ElementRef
    ) { }

    ngOnInit() {


        this.computeColumnPercentages();

        this.currentRoute = this.router.routerState.snapshot.url.split(';')[0];
        if (this.route.snapshot.params['restore']) {
            let params = this.sessionService.get(this.currentRoute);
            if (params) {
                if (params.page)
                    this.page = params.page;
                if (params.limit)
                    this.limit = params.limit;
                if (params.filter)
                    this.filter = params.filter;
            }
        }

        // get dto for type 'amberDictionary'
        if (this.fields) {
            this.fields.forEach(element => {
                if (element.config && element.config.type == 'amberDictionary') {
                    this.service.getAmberDictionary(element.config.dictionaryUrl)
                        .subscribe(
                            (data) => {
                                this.amberDictionaries[element.name as string] = data;
                            }
                        );
                }
            });
        }

        if (this.refresh) {
            this.sub = this.refresh.subscribe(_ => {
                this.onClearClicked();
                this.page = 1;
                this.fetchItems()
            });
        }

        if (this.filterRefresh) {
            this.sub = this.filterRefresh
                .subscribe(_ => {
                    this.onClearClicked();
                    this.page = 1;
                    this.fetchItems()
                });
        }

        if (this.allSelected) {
            this.subSelected = this.allSelected.subscribe(selected => {

                let allCheckbox = jQuery('.checkbox-input-wrapper input:checkbox')
                if (selected) {
                    allCheckbox.each(function () {
                        // jQuery(this).attr('checked', 'checked');
                        this.setAttribute('checked', 'checked');
                    })
                } else {
                    allCheckbox.each(function () {
                        if (this.checked) {
                            jQuery(this).removeAttr('checked');
                        }
                    })
                }

            })
        }

        if (this.bulkActionsConfig && this.bulkActionsConfig.clearSelection) {
            this.sub.add(
                this.bulkActionsConfig.clearSelection.subscribe(_ => this.onClearClicked())
            );
        }
        if (this.config && this.config.defaultSorted) {
            this.applyDefaultSorting();
        }
        this.fetchItems();

    }

    private getElHeight(element: any) {
        let elHeight = element.offsetHeight;
        let text = element.innerText;
        if ((elHeight > 50) && (element.innerText.length > 391)) {
            element.innerText = text.substring(0, 392) + '...';
        }
    }

    private computeColumnPercentages() {
        let visibleFields: DynamicGridField[] = this.fields.filter(f => !f.hidden);
        if (!visibleFields.find(f => !!f.weight)) {
            return;
        }

        let weightSum = visibleFields
            .map(f => f.weight || 1)
            .reduce((weightSum, currentWeight) => weightSum + currentWeight, 0);

        visibleFields.forEach(f => {
            let weight = f.weight || 1;
            f.width = (weight / weightSum) * 100;
        });
    }

    private toggleCol(field) {
        field.hidden = !field.hidden;
        this.computeColumnPercentages();
    }

    ngOnDestroy() {
        let data = {
            page: this.page,
            limit: this.limit,
            filter: this.filter,
        };

        this.sessionService.set(this.currentRoute, data);

        if (this.sub) {
            this.sub.unsubscribe();
        }
        if (this.subSelected) {
            this.subSelected.unsubscribe();
        }
    }

    private fetchItems() {
        let params = {
            page: this.page,
            limit: this.limit,
        };

        params['sort'] = [];
        this.currentSort.forEach((value, key) => {
            let sortKey = key;
            if (this.config && this.config.useDollarPrefixConvention) {
                sortKey = '$' + sortKey;
            }
            params['sort'].push({
                field: sortKey,
                type: value
            });
        });

        let url = this.url;
        if (this.filter) {
            url += `${url.indexOf('?') >= 0 ? '&' : '?'}filter=${encodeURIComponent(this.filter)}`;
        }

        this.isLoading = true;
        let listObservable = (this.config && this.config.listDelegate) ?
            this.service.delegateList(params, this.config.listDelegate) :
            this.service.list(params, url);

        listObservable
            .subscribe(
                data => {
                    this.extractData(data);
                    this.isLoading = false;
                },
                error => {
                    this.isLoading = false;
                }
            );
        this.refreshClicked.emit(true)
    }

    private extractData(body: any) {
        let items = body.data;
        let flags = body.flags;
        this.items = items;
        this.itemsCount = flags.size;
        this.listSize.emit(this.itemsCount);
        this.showEmptyMessage = this.filter ? false : (this.config && this.config.emptyMessage && this.itemsCount === 0);

        this.selectedIndices = [];
        this.selectedIndices.length = this.items.length;
        this.selectedCount = 0;

        if (this.bulkActionsConfig && this.bulkActionsConfig.getIds) {
            let selectedItemsIds = this.bulkActionsConfig.getIds(this.items);
            if (this.preSelectedItems) {
                if (this.preSelectedItems.length === 0)
                    this.noRecordSelected.emit(true);
                else {
                    this.preSelectedItems.forEach(psi => {
                        let index = selectedItemsIds.findIndex(it => it === psi.id);
                        if (index > -1)
                            this.checkItem(true, index);
                    })
                }
            }
        }

        // for (let i = ((this.page * this.limit) - this.limit); i < this.selectedProductsCount; i++) {
        //     let indx = i - ((this.page - 1) * this.limit);
        //     if (indx === this.limit) break;
        //     this.checkItem(true, indx);
        // }
        for (let i = 0; i < this.items.length; i++) {
            let index = this.selectedItems.findIndex(item => this.isSameItem(item, this.items[i]));
            if (index > -1) {
                this.selectedCount++;
                this.selectedIndices[i] = true;
                this.selectedItems[index] = items[i];
            }
        }

        this.refreshBulkActionsBar();
    }

    private onFilter(event: any) {
        this.filter = event.target.value;
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            this.page = 1;
            this.refreshClicked.emit(this.filter);
            this.fetchItems();
        }, this.searchDebounceTime);
    }

    private onLimitChange(event: any) {
        this.limit = event.target.value;
        this.page = 1;
        this.fetchItems();
    }

    private onPageChanged(event: any) {
        this.page = event.page;
        this.onPageChangedEvent.emit(event);
        this.fetchItems();
    }

    private onSort(field) {

        if (!this.currentSort.has(field.name)) {
            if (this.config && !this.config.allowMultiSort) {
                this.currentSort.clear();
            }
            this.currentSort.set(field.name, 1);
        }
        else {
            let value = this.currentSort.get(field.name);
            this.currentSort.set(field.name, value * -1);
        }

        this.fetchItems();
    }

    private buildRoute(route: string | ((value: string, item: T) => string), value: string, item, key: string) {
        if (typeof route === 'function') {
            return route(value, item);
        }
        let placeholder = '{{key}}';

        if (route.indexOf(placeholder) > -1 && value)
            route = route.replace(placeholder, value);


        if (key.split('.').length > 1 && route.indexOf(placeholder) > -1) {
            let props = key.split('.');
            value = item;
            for (let v of props) {
                if (value[v])
                    value = value[v];
                else {
                    value = null;
                    break;
                }
            }
            route = route.replace(placeholder, value);
        }
        return route;
    }

    private getValue(item: T, field: DynamicGridField) {

        if (field.config && field.config.type === 'composite') {
            return field.config.format(item);
        }

        let name = field.name;
        let value = null;

        if (typeof name == 'function') {
            value = name(item);
        } else {
            if (name.split('.').length > 1) {
                let props = name.split('.');
                value = item;
                for (let v of props) {
                    if (value[v])
                        value = value[v];
                    else {
                        value = null;
                        break;
                    }
                }
            } else {
                value = item[name];
            }

            if (field.formatter) {
                return field.formatter(value, item);
            }
        }
        return value;
    }

    private getFunction(item: T, field: (Item: T) => string): string {
        let value = null;
        if (typeof field == 'function') {
            value = field(item);
        }
        return value;
    }

    private getTooltipKey(item: T, value) {
        if (typeof value == 'function') {
            return value(item)
        } else {
            return value
        }
    }

    private setInnerHtml(value, el) {
        return el.innerHTML = value;
    }

    private onClearClicked() {
        this.selectedItems.splice(0, this.selectedItems.length);
        if (this.selectedIndices)
            this.selectedIndices.splice(0, this.selectedIndices.length);
        this.selectedCount = 0;

        this.refreshBulkActionsBar();
    }

    private onItemChecked(checked: boolean, index: number) {
        this.checkItem(checked, index);

        this.refreshBulkActionsBar();
    }

    private checkItem(checked: boolean, index: number) {
        this.selectedIndices[index] = checked;
        let pos = this.selectedItems.findIndex(i => this.isSameItem(i, this.items[index]));

        if (checked) {
            if (pos === -1) {
                this.selectedCount++;
                this.selectedItems.push(this.items[index]);
            }
        } else {
            if (pos > -1) {
                this.selectedCount--;
                this.selectedItems.splice(pos, 1);
            }
        }
        if (this.bulkActionsConfig && this.bulkActionsConfig.onItemSelectionChange) this.bulkActionsConfig.onItemSelectionChange(this.items[index], checked);
        this.selectedItems.length === 0 ? this.noRecordSelected.emit(true) : this.noRecordSelected.emit(false);
        this.actionClicked.emit(new ActionClickEvent(checked ? 'checked' : 'unchecked', this.items[index]));
    }

    private onAllChecked(checked: boolean) {
        this.items.forEach((item, index) => {
            if ((this.bulkActionsConfig.disableIf && this.bulkActionsConfig.disableIf(item))
                || (this.bulkActionsConfig.disableCheckbox && this.bulkActionsConfig.disableCheckbox(item))) {
                return;
            }

            this.checkItem(checked, index);
        });
        this.refreshBulkActionsBar();
    }

    private refreshBulkActionsBar() {
        if (!this.bulkActionsConfig) {
            return;
        }

        let actions = this.bulkActionsConfig.actions;
        for (let i = 0; i < actions.length; i++) {
            let action = actions[i];
            if (!(this.config && this.config.allowEmptySelection) && this.selectedItems.length === 0) {
                action.enabled = false;
            } else {
                action.enabled = action.show ? action.show(this.selectedItems) : true;
            }
        }
    }

    private isSameItem(i1: any, i2: any): boolean {
        let key = this.bulkActionsConfig.key;
        return this.getNestedValue(i1, key) === this.getNestedValue(i2, key);
    }

    private getNestedValue(obj: any, key: string): any {
        let keys = key.split('.');
        for (let k of keys) {
            obj = obj[k];
        }

        return obj;
    }

    private onBulkActionClicked(action: any) {
        if (action.enabled) {
            this.bulkActionClicked.emit(new BulkActionClickEvent(action.name, this.selectedItems));
        }
    }

    private onActionClicked(item: any, action: any) {
        this.actionClicked.emit(new ActionClickEvent(action.name, item));
    }

    private onLinkClicked(item: any) {
        this.linkClicked.emit(new ActionLinkEvent(item));
    }

    private onActionChange(event: any, item: any) {
        let action = event.target.value;
        this.actionClicked.emit(new ActionClickEvent(action, item))
    }

    private onActionCheckbox(event: any, item: any, action: any) {
        let value = jQuery(event.target).is(':checked');
        if (value) {
            this.actionClicked.emit(new ActionClickEvent(action.checked, item))
        } else {
            this.actionClicked.emit(new ActionClickEvent(action.unchecked, item));
            jQuery(this.selectedAllCheckbox.nativeElement).removeAttr('checked');
            // setTimeout(()=>{
            //     this.selectedAllCheckbox = false
            //   });
        }

    }

    private shouldShow(item: any, action: any): boolean {
        return action.show ? action.show(item) : true;
    }

    private getFormattedValueAmberDTO(val, key) {
        let result;
        if (this.amberDictionaries && this.amberDictionaries[key] && val) {
            for (let prop in this.amberDictionaries[key]) {
                if (this.amberDictionaries[key].hasOwnProperty(prop)) {
                    this.amberDictionaries[key].forEach(element => {
                        if (element.id == val) {
                            result = element.name;
                        }
                    });
                }
            }
        }
        return result || val;
    }
    private checkActionCondition(item: any, value: string) {
        let field = {
            'name': value
        }
        let result = this.getValue(item, field)
        if (result) {
            this.showActionSpan = true;
            return value
        } else {
            return null
        }

    }
    private checkIfDisabled(action: any, item: any) {
        if (action && action.disabled) {
            return action.disabled(item)
        } else {
            return false
        }
    }

    private onClearMultiSort() {
        this.currentSort.clear();
        this.fetchItems();
        this.clearSortingClicked.emit(true)
    }

    private applyDefaultSorting() {
        this.config.defaultSorted.forEach(defaultSorted => {
            let f = this.fields.find(field => field.title.toLowerCase() === defaultSorted.toLowerCase());
            if (f)
                this.currentSort.set(f.name as string, -1);
        });
    }
    private ratingChanged(event: any) {
    }

    @ViewChild('sidebarRight') sidebarRight: ElementRef;
    @ViewChild('sidebarLeft') sidebarLeft: ElementRef;

    @ViewChild('table') table: ElementRef;
    ngAfterViewInit() {
        const colWidth: number = this.calculateMargins(this.sidebarRight.nativeElement)
            + this.calculateMargins(this.sidebarLeft.nativeElement);
        jQuery(this.table.nativeElement).addClass(`col-md-${12 - colWidth}`);

        // this.htmlSpan = this.htmlSpan.nativeElement;
    }

    private calculateMargins(element: any): number {
        const regExpr = new RegExp('col-md', 'i');
        const classChildren = jQuery(element)
            .children();
        if (0 === classChildren.length) {
            return 0;
        }
        let classes = classChildren
            .attr('class')
            .split(' ')
            .filter(x => regExpr.test(x));
        if (classes.length === 0) {
            return 0;
        }
        return parseInt(classes[0].split('-')[2], 10);
    };

    private dateToString(date: any) {
        let dateString = new Date(date);
        return dateString.toDateString();
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/dynamic-grid/dynamic-grid.component.ts